<template>
  <div class="payment-page">
    <div class="payment-page-inner">
      <!-- Encabezado: identifica el contrato que se está pagando. Sin esto
                 el cliente no tiene forma de confirmar que llegó al lugar correcto. -->
      <header class="payment-header">
        <h1 class="payment-header-title">
          Pago de servicio
        </h1>
        <p
          v-if="holderName"
          class="payment-header-holder mb-0"
        >
          {{ holderName }}
        </p>
        <p
          v-if="contractCode"
          class="payment-header-code mb-0"
        >
          Contrato {{ contractCode }}
        </p>
      </header>

      <div class="payment-card">
        <div
          v-if="loading"
          class="payment-loading"
        >
          <b-spinner variant="primary" />
          <p class="mb-0 mt-1">
            Cargando su contrato…
          </p>
        </div>

        <b-alert
          v-else-if="loadError"
          variant="danger"
          show
          class="mb-0"
        >
          <div class="alert-body">
            <feather-icon
              icon="AlertCircleIcon"
              class="mr-50"
            />
            {{ loadError }}
          </div>
        </b-alert>

        <!-- El checkout es el mismo componente del portal: monto en DOP,
                     aceptación de términos, logos de marca y 3DS. Se reusa entero
                     para que esta pantalla no se desincronice de los requisitos
                     de AZUL si alguno cambia. -->
        <checkout
          v-else
          :contract="contract"
          :idcontrato="idcontrato"
        />
      </div>

      <p class="payment-footnote">
        <feather-icon
          icon="LockIcon"
          size="12"
          class="mr-25"
        />
        Conexión segura
      </p>

      <!--
        Salida explícita para quien abandona sin pagar. Esta sesión no tiene
        navegación y el router devuelve acá desde cualquier otra ruta, así que
        sin este enlace el navegador queda atrapado hasta que expire el token.
      -->
      <p
        v-if="isGuest"
        class="payment-exit"
      >
        <b-link @click="exitSession">
          Salir
        </b-link>
      </p>
    </div>
  </div>
</template>

<script>
import { BAlert, BLink, BSpinner } from "bootstrap-vue"
import Checkout from "@/views/home/Checkout.vue"
import ability from "@/libs/acl/ability"
import { initialAbility } from "@/libs/acl/config"
import { clearGuestSession, isGuestSession } from "@/auth/guestSession"

/**
 * Pantalla de pago dedicada.
 *
 * Es el destino de los enlaces que emitimos para integradores externos: el
 * cliente llega desde una app móvil ya autenticado y solo quiere pagar, así
 * que no se le muestra el detalle del contrato ni la tabla de equipos.
 *
 * Mobile-first a propósito: la mayoría del tráfico llega desde un WebView.
 */
export default {
  components: { BAlert, BLink, BSpinner, Checkout },

  data() {
    return {
      loading: true,
      loadError: "",
      /*
             * Se declaran las claves que la vista consume. El ERP responde con
             * alias en inglés y en español a la vez, así que se leen ambos:
             * cuál llega depende del endpoint.
             */
      contract: {
        idcontrato: null,
        codigo: "",
        code: "",
        apenom: "",
        full_name: "",
      },
      userData: JSON.parse(localStorage.getItem("userData") || "{}"),
    }
  },

  computed: {
    isGuest() {
      return isGuestSession()
    },
    idcontrato() {
      return Number(this.$route.params.id)
    },
    contractCode() {
      return this.contract.codigo || this.contract.code || ""
    },
    holderName() {
      return this.contract.apenom || this.contract.full_name || ""
    },
  },

  created() {
    if (Number.isNaN(this.idcontrato)) {
      this.loading = false
      this.loadError = "El enlace de pago no es válido."

      return
    }

    this.loadContract()
  },

  methods: {
    exitSession() {
      clearGuestSession()
      // CASL conserva los permisos en memoria hasta recargar; se resetea acá
      ability.update(initialAbility)
      this.$router.replace({ name: "auth-login" })
    },
    loadContract() {
      this.$http
        .get("/contrato", {
          params: {
            idcontrato: this.idcontrato,
            idusuario: this.userData.id,
          },
        })
        .then((response) => {
          const rows = response.data.data || []

          if (!rows.length) {
            this.loadError =
                            "No encontramos el contrato asociado a este enlace."

            return
          }

          const [first] = rows

          this.contract = first
          document.title = `Pago — Contrato ${this.contractCode}`
        })
        .catch((error) => {
          // 403 significa que el enlace no autoriza este contrato;
          // cualquier otro fallo se reporta como problema temporal.
          this.loadError =
                        error.response && error.response.status === 403
                          ? "Este enlace de pago ya no es válido. Vuelva a intentarlo desde la aplicación."
                          : "No pudimos cargar su contrato. Intente nuevamente en unos minutos."
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
/* Mobile-first: los estilos base son los del teléfono y solo se ensanchan
   hacia arriba. El grueso del tráfico entra por un WebView. */
.payment-page {
    min-height: 100vh;
    padding: 1rem;
    background-color: #f8f8f8;
}

.payment-page-inner {
    width: 100%;
    max-width: 30rem;
    margin: 0 auto;
}

.payment-header {
    padding: 0.5rem 0 1rem;
    text-align: center;
}

.payment-header-title {
    margin-bottom: 0.35rem;
    font-size: 1.25rem;
    font-weight: 600;
}

.payment-header-holder {
    font-weight: 600;
    color: #5e5873;
}

.payment-header-code {
    font-size: 0.875rem;
    color: #6e6b7b;
}

.payment-card {
    padding: 1.25rem;
    background-color: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 2px 12px rgba(34, 41, 47, 0.08);
}

.payment-loading {
    padding: 2rem 0;
    text-align: center;
}

.payment-exit {
    margin-top: 0.5rem;
    margin-bottom: 0;
    font-size: 0.8125rem;
    text-align: center;
}

.payment-exit {
    margin-top: 0.5rem;
    margin-bottom: 0;
    font-size: 0.8125rem;
    text-align: center;
}

.payment-footnote {
    margin-top: 1rem;
    margin-bottom: 0;
    font-size: 0.75rem;
    text-align: center;
    color: #b9b9c3;
}

/* En pantallas grandes la tarjeta se centra verticalmente en vez de quedar
   pegada arriba con una franja vacía debajo. */
@media (min-width: 768px) {
    .payment-page {
        display: flex;
        align-items: center;
        padding: 2rem;
    }

    .payment-card {
        padding: 1.75rem;
    }
}

/* El botón de pagar tiene que ser cómodo con el pulgar en un teléfono. */
@media (max-width: 575.98px) {
    .payment-card ::v-deep .btn {
        width: 100%;
        padding-top: 0.75rem;
        padding-bottom: 0.75rem;
    }

    .payment-card ::v-deep .payment-brand-row img {
        height: 24px;
    }
}
</style>
